import { action, observable, makeObservable } from 'mobx';
import { BaseViewStore } from 'core/stores';
import { TwoStepAuthenticationForm } from 'modules/membership/forms';
import { VerificationStore } from 'modules/membership/stores';
import { LookupStore } from 'modules/lookup/stores';
import { AppLoginViewStore } from 'modules/membership/stores';
import { lookupRouteName } from 'common/constants';
import { localStorageHandler } from 'core/services';
import _ from 'lodash';

class TwoStepAuthenticationViewStore extends BaseViewStore {
	@observable smsOption = true; // SMS is default option.
	@observable resendMessage = null;
	@observable timer = null;
	@observable errorMessage = null;
	@observable countryOptions = [];
	verificationCodeSent = false;
	missingPhoneNumber = false;
	phoneNumber;
	timerData;
	userId;
	userName;
	timeouts = [];

	form = new TwoStepAuthenticationForm({
		onSuccess: async () => {
			await this.validateVerificationCode();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.verificationStore = new VerificationStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.appLoginViewStore = new AppLoginViewStore(rootStore);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		this.phoneNumber = localStorageHandler.get('two-step-authentication').phoneNumber;
		this.userName = localStorageHandler.get('two-step-authentication').userName;
		this.userId = localStorageHandler.get('two-step-authentication').userId;
		this.verificationCodeSent = localStorageHandler.get('two-step-authentication').verificationCodeSent;
		this.timerData = localStorageHandler.get('two-step-authentication-timer');
		this.form.$('phoneNumber').set('value', this.phoneNumber);

		// If user is missing phone number.
		if (!this.phoneNumber) {
			this.missingPhoneNumber = true;
			this.form.$('countryCode').set('rules', 'required');
			this.form.$('phoneNumber').set('rules', 'required');
			await this.getCountryLookupOptions();
		}
		if (this.timerData != null) {
			const currentTimer = _.find(this.timerData, timerItem => {
				return timerItem.userId == this.userId;
			});
			if (currentTimer) {
				this.initializeTimer(currentTimer);
			}
		}
	};

	@action.bound
	hidePhoneNumber(phoneNumber) {
		const phoneNumberArray = phoneNumber.split(' ');
		let phoneNumberHiddenDigits;
		if (phoneNumberArray != null && phoneNumberArray[1] != null) {
			phoneNumberHiddenDigits = `${phoneNumberArray[0]} ${phoneNumberArray[1].replace(/\d(?=(?:\D*\d){4})/g, '*')}`;
		} else {
			phoneNumberHiddenDigits = phoneNumber.replace(/\d(?=(?:\D*\d){4})/g, '*');
		}
		return `+${phoneNumberHiddenDigits}`;
	}

	@action.bound
	onToggleVerificationOption(smsOption) {
		this.smsOption = smsOption;
	}

	@action.bound
	resetTimerData() {
		let timerList = localStorageHandler.get('two-step-authentication-timer');
		if (
			timerList != null &&
			_.find(timerList, timerItem => {
				return timerItem.userId == this.userId;
			})
		) {
			_.remove(timerList, timerItem => {
				return timerItem.userId == this.userId;
			});
			if (timerList.length == 0) {
				localStorageHandler.remove('two-step-authentication-timer');
			} else {
				localStorageHandler.set('two-step-authentication-timer', timerList);
			}
			this.timer = null;
			this.errorMessage = null;
		}
	}

	@action.bound
	initializeTimer(timerData) {
		this.errorMessage = timerData.message;
		const currentTime = Date.now();
		const delta = parseInt(timerData.endTime, 10) - currentTime;
		if (delta <= 0) {
			this.resetTimerData();
		} else {
			this.timer = {
				date: currentTime,
				delay: delta,
			};
		}
	}

	composeTimerList(message, currentTime, delay) {
		let timerList = localStorageHandler.get('two-step-authentication-timer');
		if (timerList == null || timerList.length == 0) {
			timerList = [];
		}
		if (
			timerList != null &&
			!_.find(timerList, timerItem => {
				return timerItem.userId == this.userId;
			})
		) {
			timerList.push({
				message: message,
				endTime: JSON.stringify(currentTime + delay),
				userId: this.userId,
			});
		}
		localStorageHandler.set('two-step-authentication-timer', timerList);
	}

	setTimerData(message) {
		const delay = 600000;
		const currentTime = Date.now();
		this.composeTimerList(message, currentTime, delay);
		this.timer = {
			date: currentTime,
			delay: delay,
		};
	}

	@action.bound
	sendVerificationCode = async resend => {
		this.suspend();
		var isPhoneNumberValid = true;
		if (this.missingPhoneNumber) {
			const phone = this.form.$('phoneNumber');
			isPhoneNumberValid = this.checkUserPhone(phone.value);
		}
		if (isPhoneNumberValid) {
			try {
				this.form.$('verificationCode').clear();
				if (resend) {
					this.errorMessage = null;
					this.resendMessage = 'TWOSTEPAUTHENTICATION.CODERESENT';
				} else {
					this.resendMessage = null;
				}
				// If user is missing phone number, use the number entered in the form, else take the number from local storage which is the one from the database.
				this.phoneNumber = this.missingPhoneNumber
					? this.form.$('countryCode').value.extra.phoneCode + ' ' + this.form.$('phoneNumber').value
					: this.phoneNumber;
				if (this.userName != null) {
					const twilioResponse = await this.verificationStore.sendVerificationCode({
						userName: this.userName,
						useSms: this.smsOption,
						phoneNumber: this.phoneNumber,
						countryId: this.missingPhoneNumber ? this.form.$('countryCode').value.extra.id : null,
						updateUserPhone: this.missingPhoneNumber,
					});
					this.verificationCodeSent = twilioResponse.isSuccess;
					this.errorMessage = null;
					this.missingPhoneNumber = false;
					if (twilioResponse.errorMessage) {
						this.errorMessage = twilioResponse.errorMessage;
						if (
							twilioResponse.errorMessage == 'TWOSTEPAUTHENTICATION.MAXCHECKATTEMPTSREACHED' ||
							twilioResponse.errorMessage == 'TWOSTEPAUTHENTICATION.MAXSENDATTEMPTSREACHED'
						) {
							this.setTimerData(twilioResponse.errorMessage);
						}
						if (twilioResponse.errorMessage == 'TWOSTEPAUTHENTICATION.INVALIDPHONENUMBER') {
							this.missingPhoneNumber = true;
						}
					}
					// Updates the local storage data.
					localStorageHandler.set('two-step-authentication', {
						userId: this.userId,
						phoneNumber: this.missingPhoneNumber ? '' : this.phoneNumber,
						userName: this.userName,
						verificationCodeSent: twilioResponse.isSuccess,
					});
				} else {
					this.rootStore.notificationStore.error('LOGINFORM.USERNOTFOUND');
				}
			} catch (err) {
				this.rootStore.notificationStore.error(err.data ? err.data : err);
			}
		}

		this.resume();
	};

	@action.bound
	validateVerificationCode = async () => {
		this.suspend();
		try {
			const { verificationCode } = this.form.values();
			this.resendMessage = null;
			const twilioResponse = await this.verificationStore.validateVerificationCode({
				verificationCode: verificationCode,
				userId: localStorageHandler.get('two-step-authentication').userId,
				phoneNumber: this.phoneNumber,
				extendedUserName: localStorageHandler.get('two-step-authentication').userName,
			});
			this.isVerificationCodeValid = twilioResponse.isSuccess;
			if (this.isVerificationCodeValid) {
				localStorageHandler.remove('two-step-authentication');
				if (twilioResponse.loginCredentials != null) {
					const app = twilioResponse.loginCredentials.organization;
					const username = twilioResponse.loginCredentials.userName;
					const password = twilioResponse.loginCredentials.password;
					const organization = app;
					const applicationIdentifier = app.trim();
					await this.appLoginViewStore.login({
						username: username.trim(),
						password,
						organization,
						app: applicationIdentifier,
					});
				} else {
					this.rootStore.notificationStore.success(['TWOSTEPAUTHENTICATION.SUCCESS']);
					this.rootStore.routerStore.goTo('master.app.membership.login');
				}
			} else {
				if (
					twilioResponse.errorMessage == 'TWOSTEPAUTHENTICATION.MAXCHECKATTEMPTSREACHED' ||
					twilioResponse.errorMessage == 'TWOSTEPAUTHENTICATION.MAXSENDATTEMPTSREACHED'
				) {
					this.verificationCodeSent = false;
					this.setTimerData(twilioResponse.errorMessage);
				}
				this.errorMessage = twilioResponse.errorMessage
					? twilioResponse.errorMessage
					: 'TWOSTEPAUTHENTICATION.INVALIDRESOURCE';
			}
			// Updates the local storage data after validation.
			localStorageHandler.set('two-step-authentication', {
				userId: this.userId,
				phoneNumber: this.phoneNumber,
				userName: this.userName,
				verificationCodeSent: twilioResponse.errorMessage
					? twilioResponse.errorMessage == 'TWOSTEPAUTHENTICATION.INVALIDVERIFICATIONCODE'
						? true
						: false
					: false,
			});
		} catch (err) {
			this.rootStore.notificationStore.error(err.data ? err.data : err);
		}
		this.resume();
	};

	@action.bound
	async getCountryLookupOptions() {
		await this.lookupStore.find({ page: 1, rpp: 300 }, lookupRouteName.country);
		this.countryOptions = _.map(this.lookupStore.data, item => {
			return { label: `${item.name} | ${item.phoneCode}`, value: item.id, extra: item };
		});
	}

	@action.bound
	onSelectChange = (value, field) => {
		this.form.$(field.path).set('value', value);
	};

	onUserPhoneChange = value => {
		// Automatically correct user phone input 2.5 seconds after user stops typing
		const highestTimeoutId = setTimeout(() => {
			this.timeouts.forEach(timeoutId => {
				if (timeoutId < highestTimeoutId) {
					clearTimeout(timeoutId);
				}
			});
			this.timeouts.push(
				setTimeout(() => {
					this.correctUserPhone(value);
				}, 2500)
			);
		}, 0);
	};

	@action.bound
	correctUserPhone = () => {
		const regex = /^\d+$/;
		var value = this.form.$('phoneNumber').value;
		if (!value.match(regex) && value) {
			value = value.replace(/\D/g, '');
			this.form.$('phoneNumber').value = value;
		}
	};

	@action.bound
	checkUserPhone = value => {
		if (value) {
			const regex = /^\d+$/;
			var phone = this.form.$('countryCode').value.extra.phoneCode + this.form.$('phoneNumber').value;
			// Allow only numbers (no whitespace and no special characters), between 7 and 15 characters.
			if (phone.length < 7 || phone.length > 15 || (!value.match(regex) && value)) {
				this.form.$('phoneNumber').invalidate('TWOSTEPAUTHENTICATION.INVALIDPHONENUMBERFORMAT');
				return false;
			} else {
				return true;
			}
		} else {
			this.form.$('phoneNumber').invalidate('MESSAGES.REQUIRED.TWOSTEPAUTHENTICATION.PHONENUMBER');
			if (!this.form.$('countryCode').value) {
				this.form.$('countryCode').invalidate('MESSAGES.REQUIRED.TWOSTEPAUTHENTICATION.COUNTRYCODE');
			}
			return false;
		}
	};
}

export default TwoStepAuthenticationViewStore;



// WEBPACK FOOTER //
// ./src/modules/membership/stores/TwoStepAuthenticationViewStore.js